import { useContext, useEffect, useMemo, useState } from "react"
import useAlert from "../../contexts/AlertContext"
import SiteContext from "../../contexts/SiteContext"
import { buscarPedidosAgendadosCliente } from "../../services/agendamento"
import Rodape from "../Global/rodape"

import {
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    Grid,
    IconButton,
    Typography,
    useMediaQuery,
    useTheme,
} from "@material-ui/core"
import CloseIcon from "@material-ui/icons/Close"
import { ObterPedido } from "../../services/functions"

export function PedidosAgendados() {
    const alertStart = useAlert()
    const { setLoading, usuarioLogado } = useContext(SiteContext)
    const [idCliente] = useState(usuarioLogado?.cliente?.id)

    const theme = useTheme()
    const mobile = useMediaQuery(theme.breakpoints.down("sm"))

    const [pedidosAgendados, setPedidosAgendados] = useState([])
    const [pedidoSelecionado, setPedidoSelecionado] = useState([])
    const [openDialog, setOpenDialog] = useState(false)
    const [pedidoHeader, setPedidoHeader] = useState(null)

    const formatarDataHora = iso => {
        if (!iso) return "-"
        const dt = new Date(iso)
        return new Intl.DateTimeFormat("pt-BR", {
            dateStyle: "short",
            timeStyle: "short",
        }).format(dt)
    }

    const statusInfo = status => {
        switch (Number(status)) {
            case 1:
                return { label: "Pendente", color: "default" }
            case 2:
                return { label: "Agendado", color: "primary" }
            default:
                return { label: "—", color: "default" }
        }
    }

    const total = useMemo(() => pedidosAgendados?.length ?? 0, [pedidosAgendados])

    const handleBuscarPedidosAgendadosCliente = async () => {
        setLoading(true)
        try {
            const response = await buscarPedidosAgendadosCliente(idCliente)
            if (response?.retornoErro) {
                alertStart(response.mensagem, "error")
            } else {
                setPedidosAgendados(Array.isArray(response) ? response : response ?? [])
            }
        } catch (error) {
            alertStart("Erro ao buscar pedidos agendados", "error")
        } finally {
            setLoading(false)
        }
    }

    const handleAbrirPedido = async agendamentoCard => {
        setLoading(true)
        try {
            setPedidoHeader(agendamentoCard)

            const response = await ObterPedido(agendamentoCard.idPedido)
            if (response?.retornoErro) {
                alertStart(response.mensagem, "error")
                return
            }

            const itens = response?.itens ?? []
            setPedidoSelecionado(Array.isArray(itens) ? itens : [])
            setOpenDialog(true)
        } catch (error) {
            alertStart("Erro ao buscar detalhes do pedido", "error")
        } finally {
            setLoading(false)
        }
    }

    const handleCloseDialog = () => {
        setOpenDialog(false)
        setPedidoSelecionado([])
        setPedidoHeader(null)
    }

    const formatMoney = v => {
        const n = Number(v ?? 0)
        return n.toLocaleString("pt-BR", { style: "currency", currency: "BRL" })
    }

    const opcaoSelecionadaLabel = itemOpcao => {
        const nome = itemOpcao?.opcaoProduto?.nome
        const qtd = Number(itemOpcao?.quantidade ?? 0)
        if (!nome || qtd <= 0) return null
        return `${nome} (x${qtd})`
    }

    const totalPedido = useMemo(() => {
        return (pedidoSelecionado ?? []).reduce((acc, item) => {
            const valor = Number(item?.valorProdutoHistorico ?? 0)
            return acc + valor
        }, 0)
    }, [pedidoSelecionado])

    useEffect(() => {
        if (!idCliente) return
        handleBuscarPedidosAgendadosCliente()
    }, [idCliente])

    return (
        <>
            <div
                style={{
                    width: "100%",
                    maxWidth: 1200,
                    margin: "24px auto",
                    padding: mobile ? "0 12px 90px" : "0 16px 90px",
                    boxSizing: "border-box",
                    overflowX: "hidden",
                }}
            >
                <Box
                    display="flex"
                    alignItems="flex-end"
                    justifyContent="space-between"
                    style={{ marginBottom: 16, flexWrap: "wrap", gap: 12 }}
                >
                    <div style={{ minWidth: 0 }}>
                        <Typography variant="h5" style={{ fontWeight: 700 }}>
                            Pedidos agendados
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                            {total} {total === 1 ? "pedido" : "pedidos"} encontrados
                        </Typography>
                    </div>
                </Box>

                <Divider style={{ marginBottom: 16 }} />

                {!pedidosAgendados || pedidosAgendados.length === 0 ? (
                    <Card style={{ borderRadius: 12 }}>
                        <CardContent>
                            <Typography variant="subtitle1" style={{ fontWeight: 700 }}>
                                Nenhum pedido agendado
                            </Typography>
                            <Typography variant="body2" color="textSecondary">
                                Quando houver novos agendamentos, eles aparecerão aqui.
                            </Typography>
                        </CardContent>
                    </Card>
                ) : (
                    <Grid container spacing={2}>
                        {pedidosAgendados.map(p => {
                            const st = statusInfo(p.status)

                            return (
                                <Grid item xs={12} sm={6} md={4} lg={3} key={p.id}>
                                    <Card
                                        onClick={() => handleAbrirPedido(p)}
                                        style={{
                                            borderRadius: 14,
                                            height: "100%",
                                            boxShadow: "0 6px 18px rgba(0,0,0,0.08)",
                                            border: "1px solid rgba(0,0,0,0.06)",
                                            overflow: "hidden",
                                            cursor: "pointer",
                                        }}
                                    >
                                        <CardContent style={{ minWidth: 0 }}>
                                            <Box
                                                display="flex"
                                                alignItems="center"
                                                justifyContent="space-between"
                                                style={{ marginBottom: 10, gap: 10, minWidth: 0 }}
                                            >
                                                <Typography
                                                    variant="subtitle1"
                                                    style={{
                                                        fontWeight: 800,
                                                        minWidth: 0,
                                                        overflow: "hidden",
                                                        textOverflow: "ellipsis",
                                                        whiteSpace: "nowrap",
                                                    }}
                                                    title={`Pedido #${p.idPedido}`}
                                                >
                                                    Pedido #{p.idPedido}
                                                </Typography>

                                                <Chip
                                                    size="small"
                                                    label={st.label}
                                                    color={st.color}
                                                    style={{ fontWeight: 700, flexShrink: 0 }}
                                                />
                                            </Box>

                                            <Box style={{ marginTop: 10 }}>
                                                <Typography variant="caption" color="textSecondary">
                                                    Entrega
                                                </Typography>
                                                <Typography
                                                    variant="body2"
                                                    style={{ fontWeight: 600 }}
                                                >
                                                    {formatarDataHora(p.dataEntrega)}
                                                </Typography>
                                            </Box>

                                            <Divider style={{ margin: "14px 0" }} />

                                            <Box
                                                display="flex"
                                                justifyContent="space-between"
                                                style={{ gap: 8 }}
                                            >
                                                <Typography variant="caption" color="textSecondary">
                                                    ID Agendamento
                                                </Typography>
                                                <Typography
                                                    variant="caption"
                                                    style={{
                                                        fontWeight: 700,
                                                        overflow: "hidden",
                                                        textOverflow: "ellipsis",
                                                        whiteSpace: "nowrap",
                                                    }}
                                                    title={String(p.id)}
                                                >
                                                    {p.id}
                                                </Typography>
                                            </Box>
                                        </CardContent>
                                    </Card>
                                </Grid>
                            )
                        })}
                    </Grid>
                )}
            </div>

            <Dialog
                open={openDialog}
                onClose={handleCloseDialog}
                fullWidth
                maxWidth="sm"
                fullScreen={mobile}
            >
                <DialogTitle disableTypography style={{ paddingRight: 48 }}>
                    <Box
                        display="flex"
                        alignItems="center"
                        justifyContent="space-between"
                        style={{ gap: 12 }}
                    >
                        <Box style={{ minWidth: 0 }}>
                            <Typography variant="h6" style={{ fontWeight: 800 }}>
                                {pedidoHeader?.idPedido
                                    ? `Pedido #${pedidoHeader.idPedido}`
                                    : "Detalhes do pedido"}
                            </Typography>

                            {pedidoHeader && (
                                <Typography
                                    variant="body2"
                                    color="textSecondary"
                                    style={{ marginTop: 2 }}
                                >
                                    Agendado: {formatarDataHora(pedidoHeader.dataInicio)} • Entrega:{" "}
                                    {formatarDataHora(pedidoHeader.dataEntrega)}
                                </Typography>
                            )}
                        </Box>

                        <IconButton onClick={handleCloseDialog} aria-label="Fechar">
                            <CloseIcon />
                        </IconButton>
                    </Box>
                </DialogTitle>

                <DialogContent dividers>
                    {!pedidoSelecionado || pedidoSelecionado.length === 0 ? (
                        <Typography variant="body2" color="textSecondary">
                            Nenhum item encontrado para este pedido.
                        </Typography>
                    ) : (
                        <Box>
                            {pedidoSelecionado.map(item => {
                                const produtoNome = item?.produto?.nome ?? "-"
                                const qtd = Number(item?.quantidade ?? 0)

                                const opcoesSelecionadas =
                                    (item?.itensOpcaoProduto ?? [])
                                        .map(opcaoSelecionadaLabel)
                                        .filter(Boolean) ?? []

                                return (
                                    <Box
                                        key={item?.id ?? `${produtoNome}-${Math.random()}`}
                                        style={{
                                            padding: "12px 0",
                                        }}
                                    >
                                        <Box
                                            display="flex"
                                            justifyContent="space-between"
                                            style={{ gap: 12 }}
                                        >
                                            <Box style={{ minWidth: 0 }}>
                                                <Typography
                                                    variant="subtitle1"
                                                    style={{ fontWeight: 800 }}
                                                >
                                                    {produtoNome}
                                                </Typography>

                                                <Typography variant="body2" color="textSecondary">
                                                    Quantidade: <b>{qtd}</b>
                                                </Typography>

                                                {opcoesSelecionadas.length > 0 && (
                                                    <Box
                                                        style={{
                                                            marginTop: 6,
                                                            display: "flex",
                                                            gap: 6,
                                                            flexWrap: "wrap",
                                                        }}
                                                    >
                                                        {opcoesSelecionadas.map(txt => (
                                                            <Chip
                                                                key={txt}
                                                                size="small"
                                                                label={txt}
                                                            />
                                                        ))}
                                                    </Box>
                                                )}

                                                {item?.observacao ? (
                                                    <Typography
                                                        variant="body2"
                                                        style={{ marginTop: 8 }}
                                                    >
                                                        <b>Obs:</b> {item.observacao}
                                                    </Typography>
                                                ) : null}
                                            </Box>

                                            <Box style={{ textAlign: "right", flexShrink: 0 }}>
                                                <Typography
                                                    variant="body2"
                                                    style={{ fontWeight: 800, marginTop: 6 }}
                                                >
                                                    {formatMoney(item?.valorProdutoHistorico ?? 0)}
                                                </Typography>
                                                <Typography variant="caption" color="textSecondary">
                                                    Subtotal
                                                </Typography>
                                            </Box>
                                        </Box>

                                        <Divider style={{ marginTop: 12 }} />
                                    </Box>
                                )
                            })}

                            <Box
                                display="flex"
                                justifyContent="space-between"
                                style={{ marginTop: 12 }}
                            >
                                <Typography variant="subtitle1" style={{ fontWeight: 900 }}>
                                    Total
                                </Typography>
                                <Typography variant="subtitle1" style={{ fontWeight: 900 }}>
                                    {formatMoney(totalPedido)}
                                </Typography>
                            </Box>
                        </Box>
                    )}
                </DialogContent>

                <DialogActions>
                    <Button onClick={handleCloseDialog} variant="contained" color="primary">
                        Fechar
                    </Button>
                </DialogActions>
            </Dialog>

            <div style={{ position: "fixed", bottom: 0, width: "100vw" }}>
                <Rodape valor="DeliveryHistorico" />
            </div>
        </>
    )
}
